import { alpha, type Theme, type ThemeOptions } from '@mui/material/styles';

const INPUT_BG = '#F2F4F9';
const INPUT_FG = '#1E2A3A';
const ERROR = '#D32F2F';
/** Placeholders are hints, never values — the same muted grey in every appearance. */
const PLACEHOLDER_FG = alpha(INPUT_FG, 0.4);

/** Theme slice for the design-system `Input` (`InputBase` + `InputLabel` wrapper). */
export function inputThemeComponents(theme: Theme): ThemeOptions['components'] {
  return {
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.SmInputLabel-root': {
            color: '#1E2A3AE5',
            fontWeight: 600,
            transition: 'none',
            marginLeft: 7,
            // `required` fields are marked with an asterisk on the label.
            '& .MuiFormLabel-asterisk': {
              color: ERROR
            },
            '&.Mui-error': {
              color: ERROR
            },
            '&.Mui-disabled': {
              color: alpha(INPUT_FG, 0.4)
            }
          }
        }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          '&.SmInputHelperText-root': {
            marginLeft: 7
          }
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          '&.SmInputBase-root': {
            // Borders and padding count toward height (plain + default); do not repeat in consumers.
            boxSizing: 'border-box',
            lineHeight: 'normal',
            'label + &': {
              marginTop: theme.spacing(2.5)
            },
            width: '100%',
            maxWidth: '100%',
            flex: 1,
            borderRadius: 10,
            backgroundColor: '#F2F4F9',
            padding: '0 18px',
            alignItems: 'center',
            border: '1px solid transparent',
            '&:focus-within': {
              backgroundColor: 'white',
              borderColor: '#D7DCEA'
            },
            '&.Mui-disabled': {
              backgroundColor: alpha(INPUT_BG, 0.7),
              boxShadow: 'none',
              cursor: 'not-allowed'
            },
            '&.Mui-error': {
              borderColor: alpha(ERROR, 0.5)
            },

            '& .MuiInputAdornment-root': {
              margin: 0,
              color: INPUT_FG,
              display: 'flex',
              alignItems: 'center',
              maxHeight: 'none',
              '& .MuiSvgIcon-root': {
                fontSize: 18,
                width: 18,
                height: 18
              }
            },
            '&.Mui-disabled .MuiInputAdornment-root': {
              color: alpha(INPUT_FG, 0.4)
            },
            '& .MuiInputAdornment-positionStart': {
              marginInlineEnd: 8
            },
            '& .MuiInputAdornment-positionEnd': {
              marginInlineStart: 8
            },
            '& .MuiInputBase-input': {
              backgroundColor: 'transparent',
              padding: '10px 0',
              outline: 'none',
              '&:disabled': {
                color: alpha(INPUT_FG, 0.4),
                WebkitTextFillColor: alpha(INPUT_FG, 0.4)
              },
              '&::placeholder': {
                color: PLACEHOLDER_FG,
                opacity: 1
              }
            },
            // Select renders its placeholder as a span, not a native ::placeholder.
            '& .SmSelect-placeholder': {
              color: PLACEHOLDER_FG
            },
            '&.SmTextAreaBase-root': {
              alignItems: 'stretch',
              padding: '10px 18px',
              '& .MuiInputBase-input': {
                padding: 0
              },
              '& .MuiInputBase-inputMultiline': {
                lineHeight: 1.4,
                resize: 'none'
              }
            }
          },
          // Every field inside a dialog or a drawer is white (2026-10-22, a standing rule): their paper
          // is `background.paper`, the same grey as the default field, which made the fields vanish in
          // each new form. Set here once so no form has to remember `appearance="plain"`. Same look
          // as plain: white with the light border; disabled stays a paler white.
          '.MuiDialog-paper &.SmInputBase-root, .MuiDrawer-paper &.SmInputBase-root': {
            backgroundColor: theme.palette.common.white,
            borderColor: '#D7DCEA',
            '&.Mui-disabled': {
              backgroundColor: alpha(theme.palette.common.white, 0.7)
            }
          },
          '&.SmInputBase-appearance-plain': {
            backgroundColor: theme.palette.common.white,
            border: `1px solid #D7DCEA`,
            '&:focus-within': {
              backgroundColor: theme.palette.common.white,
              borderColor: theme.palette.divider
            },
            '&.Mui-error': {
              borderColor: alpha(ERROR, 0.5)
            },
            '&.Mui-disabled': {
              backgroundColor: alpha(theme.palette.common.white, 0.7)
            },
            '& .MuiInputAdornment-root': {
              color: INPUT_FG,
              '& .MuiSvgIcon-root': {
                color: INPUT_FG
              }
            },
            '&.Mui-disabled .MuiInputAdornment-root': {
              color: alpha(INPUT_FG, 0.4),
              '& .MuiSvgIcon-root': {
                color: alpha(INPUT_FG, 0.4)
              }
            },
            '& .MuiInputBase-input': {
              color: INPUT_FG
            },
            '& .MuiSelect-select': {
              padding: '10px 0',
              color: INPUT_FG
            },
            '& .MuiSelect-icon': {
              color: `${INPUT_FG} !important`
            },
            '&.Mui-disabled .MuiInputBase-input, &.Mui-disabled .MuiSelect-select': {
              color: alpha(INPUT_FG, 0.4),
              WebkitTextFillColor: alpha(INPUT_FG, 0.4)
            },
            '&.Mui-disabled .MuiSelect-icon': {
              color: `${alpha(INPUT_FG, 0.4)} !important`
            }
          }
        }
      }
    }
  };
}
